import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRightIcon, SearchIcon, TriangleAlertIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useCurrentUser } from '@/features/identity';
import { AccessLogList, useWorkspaceAccessLog } from '@/features/workspaces';
import { useListWorkspaceMembers } from '@/lib/api/generated/api';
import type { AdminWorkspaceView, MemberView } from '@/lib/api/generated/model';
import { RetryButton } from '@/shared/components/RetryButton';
import { UserAvatar } from '@/shared/components/UserAvatar';
import { formatExactTime, formatRelativeTime } from '@/shared/utils/time';

import { VISIBILITY_ICON, WORKSPACES_COPY } from '../workspacesAdmin';

const WORDS = WORKSPACES_COPY.sheet;

/** How many access entries the sheet shows before "See all". */
const RECENT = 10;

const SECTION = 'border-border flex flex-col gap-3 border-t px-4 py-4';
const SECTION_TITLE =
  'text-muted-foreground text-xs font-medium tracking-wide uppercase';

/**
 * One workspace, read inside the console (spec 195 US3): what it is, who owns
 * it and who is in it, and its last access entries, with links out to the
 * workspace and its Settings. The sheet reads and does not change anything;
 * changes stay in Settings, where they are already built and logged.
 *
 * Its workspace lives in the address (`?workspace=`), as the Limits page's sheet
 * does, so Back closes it and a link can open it.
 */
export function WorkspaceSheet({
  workspace,
  onClose,
  onSeeAll,
}: {
  /** The workspace whose sheet is open; `undefined` closes it. */
  workspace: AdminWorkspaceView | undefined;
  onClose: () => void;
  /** The Activity tab, narrowed to this workspace. */
  onSeeAll: (workspaceId: string) => void;
}) {
  return (
    <Sheet
      open={workspace !== undefined}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {workspace ? (
        <SheetContent
          // Back to the row that opened it, found by its workspace rather than
          // held as a ref across refetches — when it is on screen at all.
          finalFocus={() =>
            document.querySelector<HTMLElement>(
              `tr[data-workspace-id="${workspace.id}"]`,
            )
          }
        >
          <SheetInner
            key={workspace.id}
            workspace={workspace}
            onSeeAll={onSeeAll}
          />
        </SheetContent>
      ) : null}
    </Sheet>
  );
}

function SheetInner({
  workspace,
  onSeeAll,
}: {
  workspace: AdminWorkspaceView;
  onSeeAll: (workspaceId: string) => void;
}) {
  const Icon = VISIBILITY_ICON[workspace.visibility];
  return (
    <>
      <SheetHeader>
        <SheetTitle>{workspace.name}</SheetTitle>
        <SheetDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1.5">
            <Icon aria-hidden="true" strokeWidth={1.5} className="size-3.5" />
            {WORKSPACES_COPY.visibility[workspace.visibility]}
          </span>
          <span>
            {WORDS.created}{' '}
            <time
              dateTime={workspace.created_at}
              title={formatExactTime(workspace.created_at)}
            >
              {formatRelativeTime(workspace.created_at)}
            </time>
          </span>
          <span>
            {WORDS.lastActivity}{' '}
            <time
              dateTime={workspace.last_activity_at}
              title={formatExactTime(workspace.last_activity_at)}
            >
              {formatRelativeTime(workspace.last_activity_at)}
            </time>
          </span>
        </SheetDescription>
        <div className="flex flex-wrap gap-2 pt-1">
          <Link
            to="/workspaces/$workspaceId"
            params={{ workspaceId: workspace.id }}
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            {WORDS.openWorkspace}
          </Link>
          <Link
            to="/workspaces/$workspaceId/settings"
            params={{ workspaceId: workspace.id }}
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            {WORDS.openSettings}
          </Link>
        </div>
      </SheetHeader>
      <SheetBody className="flex flex-col p-0">
        {workspace.has_active_owner ? null : (
          // The one thing to do about this workspace leads the sheet (US3.3).
          <div className="border-border flex flex-col gap-2 border-t px-4 py-4">
            <p className="text-foreground flex items-center gap-2 text-sm font-medium">
              <TriangleAlertIcon
                aria-hidden="true"
                strokeWidth={1.5}
                className="text-board-state-waiting size-4 shrink-0"
              />
              {WORDS.ownerless}
            </p>
            <Link
              to="/workspaces/$workspaceId/settings/people"
              params={{ workspaceId: workspace.id }}
              className={`${buttonVariants({ size: 'sm' })} self-start`}
            >
              {WORDS.ownerlessAction}
              <ArrowRightIcon aria-hidden="true" />
            </Link>
          </div>
        )}
        <People workspaceId={workspace.id} />
        <Recent workspace={workspace} onSeeAll={() => onSeeAll(workspace.id)} />
      </SheetBody>
    </>
  );
}

/** Owners first, then by name — the order Settings' People tab uses. */
function ordered(members: MemberView[]): MemberView[] {
  return [...members].sort((a, b) => {
    if (a.role !== b.role) return a.role === 'owner' ? -1 : 1;
    return a.user.display_name.localeCompare(b.user.display_name, undefined, {
      sensitivity: 'base',
    });
  });
}

function People({ workspaceId }: { workspaceId: string }) {
  const query = useListWorkspaceMembers(workspaceId);
  const response = query.data;
  const members = response?.status === 200 ? response.data.members : undefined;
  const failed =
    query.isError || (response !== undefined && response.status !== 200);
  const [needle, setNeedle] = useState('');
  const shown = useMemo(() => {
    const text = needle.trim().toLocaleLowerCase();
    return ordered(members ?? []).filter(
      (member) =>
        text === '' ||
        member.user.display_name.toLocaleLowerCase().includes(text) ||
        member.user.email.toLocaleLowerCase().includes(text),
    );
  }, [members, needle]);

  return (
    <section aria-labelledby="workspace-sheet-people" className={SECTION}>
      <h3 id="workspace-sheet-people" className={SECTION_TITLE}>
        {WORDS.people}
        {members ? (
          <span className="ms-1.5 tabular-nums normal-case">
            {members.length}
          </span>
        ) : null}
      </h3>
      {failed ? (
        <div className="flex items-center gap-3">
          <p className="text-muted-foreground text-sm">{WORDS.peopleFailed}</p>
          <RetryButton onRetry={() => void query.refetch()} />
        </div>
      ) : members === undefined ? (
        <p className="text-muted-foreground text-sm">{WORDS.loading}</p>
      ) : (
        <>
          <label className="relative">
            <span className="sr-only">{WORDS.searchPeople}</span>
            <SearchIcon
              aria-hidden="true"
              strokeWidth={1.5}
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            />
            <Input
              type="search"
              value={needle}
              placeholder={WORDS.searchPeople}
              onChange={(event) => setNeedle(event.target.value)}
              className="ps-8"
            />
          </label>
          {shown.length === 0 ? (
            <p className="text-muted-foreground text-sm">{WORDS.noPeople}</p>
          ) : (
            <ul
              aria-label={WORDS.people}
              className="divide-border flex flex-col divide-y"
            >
              {shown.map((member) => (
                <li
                  key={member.user.id}
                  className="flex items-center gap-2.5 py-2"
                >
                  <UserAvatar
                    id={member.user.id}
                    displayName={member.user.display_name}
                    email={member.user.email}
                    className={
                      member.is_active ? undefined : 'opacity-50 grayscale'
                    }
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span
                      className={
                        member.is_active
                          ? 'text-foreground truncate text-sm'
                          : 'text-muted-foreground truncate text-sm line-through'
                      }
                    >
                      {member.user.display_name}
                    </span>
                    <span className="text-muted-foreground truncate text-xs">
                      {member.user.email}
                    </span>
                  </span>
                  {member.is_active ? null : (
                    <Badge variant="outline">
                      {WORKSPACES_COPY.deactivated}
                    </Badge>
                  )}
                  <Badge
                    variant={member.role === 'owner' ? 'secondary' : 'outline'}
                  >
                    {member.role === 'owner' ? WORDS.owner : WORDS.member}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function Recent({
  workspace,
  onSeeAll,
}: {
  workspace: AdminWorkspaceView;
  onSeeAll: () => void;
}) {
  const log = useWorkspaceAccessLog(workspace.id, true);
  const recent = log.entries.slice(0, RECENT);
  // Only an admin who is not in it is recorded opening it; a member's read is a
  // member's. The same members read as the people list above, so no second request.
  const me = useCurrentUser().data?.id;
  const members = useListWorkspaceMembers(workspace.id).data;
  const recorded =
    workspace.visibility === 'private' &&
    members?.status === 200 &&
    me !== undefined &&
    !members.data.members.some((member) => member.user.id === me);

  return (
    <section aria-labelledby="workspace-sheet-recent" className={SECTION}>
      <div className="flex items-center justify-between gap-2">
        <h3 id="workspace-sheet-recent" className={SECTION_TITLE}>
          {WORDS.recent}
        </h3>
        {/* Foreground, underlined: no Light Blue small text in dark mode (WD-04). */}
        <Button
          variant="link"
          size="sm"
          className="text-foreground h-auto px-0 underline"
          onClick={onSeeAll}
        >
          {WORDS.seeAll}
        </Button>
      </div>
      {recorded ? (
        <p className="text-muted-foreground text-xs">{WORDS.recorded}</p>
      ) : null}
      {log.isPending ? (
        <p className="text-muted-foreground text-sm">{WORDS.loading}</p>
      ) : log.isError ? (
        <div className="flex items-center gap-3">
          <p className="text-muted-foreground text-sm">{WORDS.logFailed}</p>
          <RetryButton onRetry={log.retry} />
        </div>
      ) : recent.length === 0 ? (
        <p className="text-muted-foreground text-sm">{WORDS.noEntries}</p>
      ) : (
        <AccessLogList
          entries={recent}
          label={WORDS.recent}
          hasMore={false}
          loadingMore={false}
          onMore={() => undefined}
        />
      )}
    </section>
  );
}
